import type { FavoritePageActionDependencies } from './types'

export function createFavoritePageActions({
  apiClient,
  invalidateFavouriteProducts,
  logger,
  store,
}: FavoritePageActionDependencies) {
  async function loadFavourites(contextItemId: string) {
    const state = store.getState()

    if (state.loadStatus === 'loading') {
      return
    }

    state.setIn('loadStatus', 'loading')
    state.setIn('removingProductIds', [])
    state.setIn('results', [])
    state.setIn('allFavouritesRemovalStatus', 'idle')

    try {
      const results = await apiClient.getFavouriteProductsResponse(contextItemId)
      const updatedState = store.getState()
      updatedState.setIn('loadStatus', 'success')
      updatedState.setIn('removingProductIds', [])
      updatedState.setIn('results', results)
    }
    catch (error) {
      logger.logError(toError(error), { action: 'favoritePageLoaded' })
      store.getState().setIn('loadStatus', 'error')
    }
  }

  async function pageLoaded(contextItemId: string) {
    await loadFavourites(contextItemId)
  }

  async function favouritesRetryRequested(contextItemId: string) {
    await loadFavourites(contextItemId)
  }

  async function favouriteRemovalRequested(contextItemId: string, productId: number) {
    const state = store.getState()

    if (state.removingProductIds.includes(productId)) {
      return
    }

    state.setIn('removingProductIds', [...state.removingProductIds, productId])

    try {
      await apiClient.removeProductFromFavourites({
        contextItemId,
        productId: String(productId),
      })

      const updatedState = store.getState()
      updatedState.setIn('removingProductIds', updatedState.removingProductIds.filter(id => id !== productId))
      updatedState.setIn('results', updatedState.results.filter(product => product.id !== productId))

      try {
        await invalidateFavouriteProducts()
      }
      catch (error) {
        logger.logError(toError(error), {
          action: 'favouriteProductsInvalidationRequested',
          productId,
        })
      }
    }
    catch (error) {
      logger.logError(toError(error), {
        action: 'favouriteRemovalRequested',
        productId,
      })
      const updatedState = store.getState()
      updatedState.setIn('removingProductIds', updatedState.removingProductIds.filter(id => id !== productId))
    }
  }

  async function allFavouritesRemovalRequested() {
    const state = store.getState()

    if (state.allFavouritesRemovalStatus === 'loading') {
      return
    }

    state.setIn('allFavouritesRemovalStatus', 'loading')

    try {
      await apiClient.removeAllFavourites()
      const updatedState = store.getState()
      updatedState.setIn('loadStatus', 'success')
      updatedState.setIn('removingProductIds', [])
      updatedState.setIn('results', [])
      updatedState.setIn('allFavouritesRemovalStatus', 'idle')

      try {
        await invalidateFavouriteProducts()
      }
      catch (error) {
        logger.logError(toError(error), {
          action: 'favouriteProductsInvalidationRequested',
        })
      }
    }
    catch (error) {
      logger.logError(toError(error), { action: 'allFavouritesRemovalRequested' })
      store.getState().setIn('allFavouritesRemovalStatus', 'error')
    }
  }

  return {
    allFavouritesRemovalRequested,
    favouriteRemovalRequested,
    favouritesRetryRequested,
    pageLoaded,
  }
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}
